import React, { Component } from "reactn";
import styled from "styled-components";

import { THUMBNAIL_SIZES } from "common/constants";
import { ToggleSwitch } from "js/Components/ToggleSwitch";
import getLogger from "js/core/logger";
import { ds } from "js/core/models/dataService";
import Thumbnails from "js/core/models/thumbnails";
import ux from "js/core/services/userExperiences";
import getLocalStorage from "js/core/utilities/localStorage";
import { t } from "js/i18n";
import InfoToolTip from "js/react/components/InfoToolTip";
import { NoMatchTemplatesNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { UIPane } from "js/react/components/UiComponents";
import { gallerySlidesDataService } from "js/react/views/AddSlide/DataServices";
import { SlideSearchPreview } from "js/react/views/AddSlide/Panes/Components/SlideSearchPreview";
import {
    InnerWhiteFrameContainer,
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer
} from "./Components/SearchBox";
import { Thumbnail, ThumbnailContainer, ThumbnailGrid } from "./Components/ThumbnailGrid";

const logger = getLogger();

const PREVIEW_WITH_BRAND_KIT_STORAGE_KEY = "inspirationGalleryPreviewWithBrandKit";

const GalleryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    min-height: 31px;
    padding: 20px 20px 0;
    background: white;
`;

const GalleryTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #111;
`;

const BrandKitToggle = styled.label`
    display: flex;
    align-items: center;
    gap: 14px;
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 12px;
    font-weight: 600;
    line-height: 15px;
    cursor: pointer;
`;

const BrandKitTooltipText = styled.span`
    color: #fff;
    font-family: "Source Sans Pro";
    font-size: 10px;
    font-weight: 400;
    line-height: 125%;
`;

// Themed thumbnails are rendered with the theme of the open presentation, so there is nothing to preview without one
function canPreviewWithBrandKit() {
    const presentation = ds.selection.presentation;
    return !!presentation && !presentation.get("isDummy");
}

export class SlideGalleryPane extends Component {
    _isMounted = false;

    constructor(props) {
        super(props);

        this.state = {
            isLoaded: false,
            query: this.global.searchSlidesQuery ?? "",
            searchResults: [],
            previewWithBrandKit: getLocalStorage().getItem(PREVIEW_WITH_BRAND_KIT_STORAGE_KEY) === "true"
        };
    }

    componentDidMount() {
        this._isMounted = true;
        const { query } = this.state;
        // Pull default result for initial query
        this.fetchSearchResults(query);

        ux.addSlideToPresentation.update({ subtype: ux.Subtype[ux.Type.ADD_SLIDES_TO_PRESENTATION].COPY_GALLERY_SLIDES });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    fetchSearchResults = async (query = null) => {
        const searchResults = await gallerySlidesDataService.search({ fullTextSearchQuery: query, size: 500 });
        if (!this._isMounted) {
            return;
        }
        this.setState({
            searchResults,
            isLoaded: true
        });
    }

    handleSearch = query => {
        this.setState(
            {
                query,
                isLoaded: false
            },
            () => this.fetchSearchResults(query)
        );
    }

    handleTogglePreviewWithBrandKit = previewWithBrandKit => {
        getLocalStorage().setItem(PREVIEW_WITH_BRAND_KIT_STORAGE_KEY, String(previewWithBrandKit));
        this.setState({ previewWithBrandKit });
    }

    render() {
        const { query } = this.state;
        return (
            <UIPane>
                <SearchBarContainer>
                    <SearchBarInnerContainer>
                        <SlideSearchInput
                            query={query}
                            onSearch={this.handleSearch}
                            placeholder={t("Search for inspiration...")}
                            showShortCuts
                        />
                    </SearchBarInnerContainer>
                </SearchBarContainer>
                <UIPaneResultsContainer>
                    {this.renderSlides()}
                </UIPaneResultsContainer>
            </UIPane>
        );
    }

    renderHeader() {
        const { previewWithBrandKit } = this.state;

        return (
            <GalleryHeader>
                <GalleryTitle>{t("Start from our Inspiration Gallery")}</GalleryTitle>
                {canPreviewWithBrandKit() &&
                    <InfoToolTip
                        title={<BrandKitTooltipText>{t("Preview slides as they’ll appear in your deck. Added slides automatically adopt its Brand Kit.")}</BrandKitTooltipText>}
                        gap={10}
                        placement="top"
                        label={
                            <BrandKitToggle>
                                <ToggleSwitch
                                    value={previewWithBrandKit}
                                    onChange={this.handleTogglePreviewWithBrandKit}
                                    data-test-id="preview-with-brand-kit-switch"
                                />
                                {t("Preview with presentation Brand Kit")}
                            </BrandKitToggle>
                        }
                    />
                }
            </GalleryHeader>
        );
    }

    renderSlides() {
        const { onSelected, onAddBlankClassicSlide } = this.props;
        const { isLoaded, searchResults, query, previewWithBrandKit } = this.state;

        if (!isLoaded) {
            return <Spinner />;
        }

        const showThemeThumbnails = previewWithBrandKit && canPreviewWithBrandKit();

        if (!query) {
            return (
                <>
                    {this.renderHeader()}
                    <InnerWhiteFrameContainer>
                        <ThumbnailGrid
                            columns={3}
                            colGap={30}
                            rowGap={20}
                            items={searchResults}
                            thumbnailClass={SlideGalleryPreviewThumbnail}
                            onItemSelected={onSelected}
                            showThemeThumbnails={showThemeThumbnails}
                        />
                    </InnerWhiteFrameContainer>
                </>
            );
        }

        if (searchResults.length === 0) {
            return (
                <NoMatchTemplatesNotice
                    onAddClassicSlide={onAddBlankClassicSlide}
                />
            );
        }

        return (
            <>
                {this.renderHeader()}
                <SlideSearchPreview
                    expanded={true}
                    allowCollapse={false}
                    title=""
                    thumbnail={SlideGalleryPreviewThumbnail}
                    items={searchResults}
                    onSelected={onSelected}
                    showThemeThumbnails={showThemeThumbnails}
                />
            </>
        );
    }
}

export default SlideGalleryPane;

export class SlideGalleryPreviewThumbnail extends Component {
    _isMounted = false;

    constructor() {
        super();

        this.state = {
            url: "",
            isVisible: false
        };

        this.ref = React.createRef();
    }

    componentDidMount() {
        this._isMounted = true;
        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting === true) {
                this.observer.disconnect();
                if (!this._isMounted) {
                    return;
                }
                this.setState({ isVisible: true });
            }
        });

        this.observer.observe(this.ref.current);
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.observer.disconnect();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { showThemeThumbnails, item } = this.props;
        const { isVisible } = this.state;

        if (!isVisible) {
            return;
        }

        if (prevProps.showThemeThumbnails !== showThemeThumbnails || prevState.isVisible !== isVisible) {
            this.loadThumbnail()
                .catch(err => logger.error(err, "[SlideGalleryPane] this.loadThumbnail() failed", { id: item?.id }));
        }
    }

    async loadThumbnail() {
        const { item, highQuality, showThemeThumbnails } = this.props;

        if (!this._isMounted) {
            return;
        }

        // Toggling the theme starts a new load while the previous one may still be rendering
        const loadId = this.loadId = (this.loadId ?? 0) + 1;

        this.setState({ url: "" });

        let url;
        if (showThemeThumbnails && ds.selection.presentation && !ds.selection.presentation.get("isDummy")) {
            const presentationId = ds.selection.presentation.id;
            const firstSlideModifiedAt = ds.selection.presentation.get("firstSlideModifiedAt");
            url = await Thumbnails.getSignedUrlAndLoad(item.id, Math.max(item.modifiedAt, firstSlideModifiedAt), presentationId, THUMBNAIL_SIZES[highQuality ? "large" : "small"].suffix);
        } else {
            url = await Thumbnails.getSignedUrlAndLoad(item.id, item.modifiedAt, null, THUMBNAIL_SIZES[highQuality ? "large" : "small"].suffix);
        }

        if (!this._isMounted || loadId !== this.loadId) {
            return;
        }

        this.setState({ url });
    }

    render() {
        const { item, onClick, selected } = this.props;
        const { url } = this.state;

        return (
            <ThumbnailContainer ref={this.ref}>
                <Thumbnail onClick={onClick} url={url} selected={selected} showSpinner={true} style={{ border: "solid 1px #ccc", boxShadow: "0px 0px 20px rgba(0,0,0,0.05)" }} />
                {item.name && <label>{item.name}</label>}
            </ThumbnailContainer>
        );
    }
}
